import { createApiV2Client } from '../../services/apiV2.js';
import {
    formatEnumLabel,
    translateWithFallback,
} from '../../i18n/translationUtils.js';
import { canDisplayAuditScores } from '../../utils/auditVisibility.js';
import {
    isAuditComplete,
    resolveAuditStatus,
} from '../../utils/auditStatus.js';
import { normalizeScore } from '../../utils/scoreUtils.js';

const APP_REFERENCE_PATHS = {
    AUDIT_RECORD: 'audit-record',
    AUDIT_GAP: 'audit-gap',
    ACTION_POINT: 'action-point',
};
const REFERENCE_TYPES = new Set(['FILE', ...Object.keys(APP_REFERENCE_PATHS)]);
const SAFE_ACTIVITY_LABELS = [
    /^Understanding(?: your| the)? question$/i,
    /^Searching audit records$/i,
    /^Searching audit gaps$/i,
    /^Searching action points$/i,
    /^Found \d+ (?:matching )?(?:audit )?records?$/i,
    /^Reviewing audit$/i,
    /^Reviewing audit requirements$/i,
    /^Checking audit gap$/i,
    /^Checking (?:unresolved )?action points?$/i,
    /^Couldn’t (?:search audit records|review audit|check audit gap|check action point)$/i,
    /^Couldn’t (?:search audit gaps|search action points|review audit requirements)$/i,
];

const safeActivityLabel = (label) => {
    const value = String(label || '').trim();
    return SAFE_ACTIVITY_LABELS.some((pattern) => pattern.test(value))
        ? value
        : 'Reviewing audit information';
};

const safeContext = (context = {}) => {
    const result = {};
    const siteId = Number(context.siteId);
    if (Number.isSafeInteger(siteId) && siteId > 0) result.siteId = siteId;
    if (typeof context.auditHash === 'string' && context.auditHash.trim()) {
        result.auditHash = context.auditHash.trim();
    }
    return result;
};

const normalizeReference = (reference) => {
    if (!reference || !REFERENCE_TYPES.has(reference.type) || !reference.hash) {
        return null;
    }
    const normalized = { type: reference.type, hash: String(reference.hash) };
    const label = reference.label || reference.title;
    if (label) normalized.label = String(label);
    if (/^S[1-9][0-9]*$/.test(String(reference.citationId || ''))) {
        normalized.citationId = String(reference.citationId);
    }
    if (reference.type === 'FILE') {
        if (reference.title) normalized.title = String(reference.title);
        if (Number.isFinite(reference.pageNumber)) {
            normalized.pageNumber = reference.pageNumber;
        }
        if (Number.isFinite(reference.totalPages)) {
            normalized.totalPages = reference.totalPages;
        }
        if (Number.isFinite(reference.index))
            normalized.index = reference.index;
    }
    return normalized;
};

export function normalizeAuditAnnotations(annotations) {
    if (!annotations || annotations.version !== 1) {
        return { version: 1, references: [], activities: [] };
    }
    return {
        version: 1,
        references: Array.isArray(annotations.references)
            ? annotations.references.map(normalizeReference).filter(Boolean)
            : [],
        activities: Array.isArray(annotations.activities)
            ? annotations.activities
                  .filter((activity) => activity?.id && activity?.label)
                  .map((activity) => ({
                      id: String(activity.id),
                      label: safeActivityLabel(activity.label),
                      status:
                          activity.status === 'failed' ? 'failed' : 'complete',
                  }))
            : [],
        ...(typeof annotations.asOfDate === 'string'
            ? { asOfDate: annotations.asOfDate }
            : {}),
        ...(typeof annotations.timeZone === 'string'
            ? { timeZone: annotations.timeZone }
            : {}),
    };
}

export function upsertAuditReference(references = [], candidate) {
    const normalized = normalizeReference(candidate);
    if (!normalized) return [...references];
    const next = references.map(normalizeReference).filter(Boolean);
    const index = next.findIndex(
        (reference) =>
            reference.type === normalized.type &&
            reference.hash === normalized.hash
    );
    if (index >= 0) next.splice(index, 1, normalized);
    else next.push(normalized);
    return next;
}

export function renderAuditCitationMarkers(content, references = []) {
    const byCitationId = new Map(
        references
            .map(normalizeReference)
            .filter((reference) => reference?.citationId)
            .map((reference) => [reference.citationId, reference])
    );
    return String(content || '').replace(
        /\[\[audit-ref:(S[1-9][0-9]*)\]\]/g,
        (_marker, citationId) => {
            if (!byCitationId.has(citationId)) return '';
            const number = citationId.slice(1);
            return `<button type="button" class="audit-inline-citation" data-citation-id="${citationId}" aria-label="Open audit reference ${number}">[${number}]</button>`;
        }
    );
}

export function applyActivityEvent(activities, activity) {
    if (
        !activity?.id ||
        !activity?.label ||
        !['active', 'complete', 'failed'].includes(activity.status)
    ) {
        return { activities: [...activities], expanded: true };
    }
    const next = [...activities];
    const normalized = {
        id: String(activity.id),
        label: safeActivityLabel(activity.label),
        status: activity.status,
    };
    const index = next.findIndex((item) => item.id === normalized.id);
    if (index >= 0) next.splice(index, 1, normalized);
    else next.push(normalized);
    return {
        activities: next,
        expanded: next.some((item) => item.status === 'active'),
    };
}

export function splitReferences(references = []) {
    const normalized = references.map(normalizeReference).filter(Boolean);
    return {
        fileCitations: normalized
            .filter((reference) => reference.type === 'FILE')
            .map((reference) => ({
                file_hash: reference.hash,
                original_filename:
                    reference.title || reference.label || 'Referenced file',
                ...(reference.pageNumber !== undefined
                    ? { page_number: reference.pageNumber }
                    : {}),
                ...(reference.totalPages !== undefined
                    ? { total_pages: reference.totalPages }
                    : {}),
                ...(reference.index !== undefined
                    ? { index: reference.index }
                    : {}),
            })),
        appReferences: normalized.filter(
            (reference) => reference.type !== 'FILE'
        ),
    };
}

export function groupAuditReferences(references = []) {
    const seen = new Set();
    const unique = splitReferences(references).appReferences.filter(
        (reference) => {
            const key = `${reference.type}:${reference.hash}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        }
    );
    return [
        { type: 'AUDIT_RECORD', label: 'Audits' },
        { type: 'AUDIT_GAP', label: 'Gaps' },
        { type: 'ACTION_POINT', label: 'Action points' },
    ]
        .map((group) => ({
            ...group,
            references: unique.filter(
                (reference) => reference.type === group.type
            ),
        }))
        .filter((group) => group.references.length);
}

const AUDIT_STATUS_LABEL_KEYS = {
    COMPLETE: 'patty.auditReferences.statuses.complete',
    DRAFT: 'patty.auditReferences.statuses.inProgress',
    SCHEDULED: 'patty.auditReferences.statuses.scheduled',
};

export function resolveAuditStatusLabel(audit, translateFn = null) {
    const status = resolveAuditStatus(audit);
    if (!status) return null;
    const labelKey = AUDIT_STATUS_LABEL_KEYS[status];
    if (labelKey) {
        return translateWithFallback(
            translateFn,
            labelKey,
            formatEnumLabel(status)
        );
    }
    return formatEnumLabel(status);
}

export function auditReferenceScore(audit) {
    if (!canDisplayAuditScores(audit) || !isAuditComplete(audit)) return null;
    if (audit.scoreStatus !== undefined) {
        return audit.scoreStatus === 'SCORED'
            ? normalizeScore(audit.scorePercent)
            : null;
    }
    if (audit.score_override != null)
        return normalizeScore(audit.score_override);
    if (
        audit.record_type === 'audit-report' ||
        String(audit.audit_type || audit.type || '').toUpperCase() === 'REPORT'
    ) {
        return null;
    }
    if (audit.form_score != null && audit.form_max_score > 0) {
        return normalizeScore(
            Math.round((audit.form_score / audit.form_max_score) * 100)
        );
    }
    // Legacy compact DTOs merge override percentages and earned points into
    // `score`. Without their provenance it cannot safely be rendered.
    return null;
}

export function normalizeAuditReferenceRecord(record) {
    if (!record || typeof record !== 'object') return null;
    return {
        ...record,
        // Snake-case mirrors of the v2 DTO keep PattyAuditRecordReadOnly and
        // auditReferenceScore working against both old and new payloads.
        ...(record.formScore !== undefined && record.form_score === undefined
            ? { form_score: record.formScore }
            : {}),
        ...(record.formMaxScore !== undefined &&
        record.form_max_score === undefined
            ? { form_max_score: record.formMaxScore }
            : {}),
        ...(record.scoreOverride !== undefined &&
        record.score_override === undefined
            ? { score_override: record.scoreOverride }
            : {}),
        ...(record.displayScores !== undefined &&
        record.display_scores === undefined
            ? { display_scores: record.displayScores }
            : {}),
        ...(record.completedAt !== undefined &&
        record.completed_at === undefined
            ? { completed_at: record.completedAt }
            : {}),
        ...(record.conductedBy !== undefined &&
        record.auditor_name === undefined
            ? { auditor_name: record.conductedBy }
            : {}),
        ...(record.reviewedBy !== undefined &&
        record.reviewer_name === undefined
            ? { reviewer_name: record.reviewedBy }
            : {}),
        ...(record.assignedTo !== undefined
            ? {
                  assignedTo: record.assignedTo,
                  ...(record.accountable_name === undefined
                      ? { accountable_name: record.assignedTo }
                      : {}),
              }
            : {}),
    };
}

export function referencePath(reference) {
    const type = APP_REFERENCE_PATHS[reference?.type];
    if (!type || !reference?.hash) {
        throw new TypeError('A valid application reference is required.');
    }
    return `/audit-agent/references/${type}/${encodeURIComponent(
        reference.hash
    )}`;
}

export function auditFilePath(auditHash, fileHash) {
    if (!auditHash || !fileHash) {
        throw new TypeError('An audit hash and file hash are required.');
    }
    return `/audit-agent/references/audit-record/${encodeURIComponent(
        auditHash
    )}/files/${encodeURIComponent(fileHash)}`;
}

export function normalizeReferenceResponse(type, response) {
    const recordByType = {
        AUDIT_RECORD: response?.auditRecord || response?.audit,
        AUDIT_GAP:
            response?.auditGap && typeof response.auditGap === 'object'
                ? response.auditGap
                : null,
        ACTION_POINT:
            response?.actionPoint && typeof response.actionPoint === 'object'
                ? response.actionPoint
                : null,
    };
    const source =
        response?.data ||
        response?.record ||
        response?.reference ||
        recordByType[type] ||
        response ||
        null;
    if (!source || typeof source !== 'object') {
        return { record: null, linkedActionPoints: [] };
    }

    if (type === 'AUDIT_RECORD') {
        const siteName =
            typeof source.site === 'string'
                ? source.site
                : source.site?.name || source.site_name;
        const templateName =
            typeof source.template === 'string'
                ? source.template
                : source.template?.name;
        return {
            record: normalizeAuditReferenceRecord({
                ...source,
                ...(siteName ? { site_name: siteName } : {}),
                ...(templateName ? { template_name: templateName } : {}),
                ...(source.type && !source.audit_type
                    ? { audit_type: source.type }
                    : {}),
            }),
            linkedActionPoints: [],
        };
    }

    if (type === 'AUDIT_GAP') {
        const actionPoints = Array.isArray(source.actionPoints)
            ? source.actionPoints
            : Array.isArray(response?.linkedActionPoints)
            ? response.linkedActionPoints
            : [];
        return {
            record: source,
            linkedActionPoints: actionPoints.map((actionPoint) => ({
                ...normalizeAuditReferenceRecord(actionPoint),
                action_point:
                    actionPoint.action_point || actionPoint.actionPoint || null,
            })),
        };
    }

    if (type === 'ACTION_POINT') {
        return {
            record: normalizeAuditReferenceRecord({
                ...source,
                action_point: source.action_point || source.actionPoint || null,
                completion_date:
                    source.completion_date || source.completionDate || null,
            }),
            linkedActionPoints: [],
        };
    }

    return { record: source, linkedActionPoints: [] };
}

export function buildAuditThreadRequest(threadHash, message, context = {}) {
    const body = { message };
    const normalizedContext = safeContext(context);
    if (Object.keys(normalizedContext).length) body.context = normalizedContext;
    return {
        path: threadHash
            ? `/audit-agent/threads/${encodeURIComponent(threadHash)}/messages`
            : '/audit-agent/threads',
        body,
    };
}

export function createAuditAgentApi({ apiRoot, fetchImpl } = {}) {
    const client = createApiV2Client({ apiRoot, fetchImpl });
    return {
        listThreads: () => client.request('/audit-agent/threads'),
        getThread: (threadHash) =>
            client.request(
                `/audit-agent/threads/${encodeURIComponent(threadHash)}`
            ),
        getReference: (reference) => client.request(referencePath(reference)),
        getAuditFile: (auditHash, fileHash, { download = false } = {}) =>
            client.request(auditFilePath(auditHash, fileHash), {
                query: download ? { download: true } : null,
                responseType: 'blob',
            }),
        streamMessage({
            threadHash = null,
            message,
            context,
            signal,
            onEvent,
        }) {
            const request = buildAuditThreadRequest(
                threadHash,
                message,
                context
            );
            return client.stream(request.path, {
                method: 'POST',
                body: request.body,
                signal,
                timeoutMs: 120000,
                onEvent,
            });
        },
    };
}

export { safeContext };
